import { defineMutators } from "@rocicorp/zero";

import { usersMutators } from "./users";
import { userPresenceMutators } from "./user_presence";
import { workspacesMutators, workspaceMembersMutators } from "./workspaces";
import { channelsMutators, channelMembersMutators } from "./channels";
import {
  messagesMutators,
  reactionsMutators,
  linkPreviewsMutators,
  attachmentsMutators,
} from "./messages";
import {
  notificationKeywordsMutators,
  threadFollowersMutators,
  pinsMutators,
  channelPostingPermissionsMutators,
  notificationPreferencesMutators,
  dndSettingsMutators,
  channelNotificationOverridesMutators,
  notificationEventsMutators,
} from "./notifications";
import { invitesMutators, declinedInvitesMutators } from "./invites";
import {
  listsMutators,
  listItemsMutators,
  listItemCommentsMutators,
} from "./lists";
import { actionsMutators } from "./actions";
import { clientSpacesMutators } from "./client-spaces";
import { coworkersMutators } from "./coworkers";
import { workspaceJoinRequestsMutators } from "./workspace-join-requests";
import { communitySignalsMutators } from "./community-signals";
import { uiFlagsMutators } from "./ui-flags";
import { threadQaMutators } from "./thread-qa";
// Workspace-slug validator — re-exported below so the server bootstrap route
// can gate reserved slugs without duplicating the reserved list.
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { assertWorkspaceSlugValid } from "./helpers";

export const mutators = defineMutators({
  users: usersMutators,
  user_presence: userPresenceMutators,
  workspaces: workspacesMutators,
  workspace_members: workspaceMembersMutators,
  channels: channelsMutators,
  channel_members: channelMembersMutators,
  messages: messagesMutators,
  reactions: reactionsMutators,
  link_previews: linkPreviewsMutators,
  attachments: attachmentsMutators,
  notification_keywords: notificationKeywordsMutators,
  thread_followers: threadFollowersMutators,
  pins: pinsMutators,
  channel_posting_permissions: channelPostingPermissionsMutators,
  invites: invitesMutators,
  declined_invites: declinedInvitesMutators,
  notification_preferences: notificationPreferencesMutators,
  dnd_settings: dndSettingsMutators,
  user_ui_flags: uiFlagsMutators,
  channel_notification_overrides: channelNotificationOverridesMutators,
  lists: listsMutators,
  list_items: listItemsMutators,
  list_item_comments: listItemCommentsMutators,
  notification_events: notificationEventsMutators,
  actions: actionsMutators,
  client_spaces: clientSpacesMutators,
  coworkers: coworkersMutators,
  workspace_join_requests: workspaceJoinRequestsMutators,
  community_signals: communitySignalsMutators,
  thread_qa: threadQaMutators,

  // Client space invite links are now handled by the unified `invites` mutators.
});

// ── D2.0: re-export core helpers + schemas for the server-mutator extension ──
// `server/server-mutators.ts` calls `defineMutators(clientMutators, overrides)`
// where each override re-uses the same Zod schema as the client mutator AND
// calls the same `*Core` body. Exposing them here keeps the import surface
// flat: `import { messageInsertCore, ... } from "@ano/shared/mutators"`.
export {
  messageInsertSchema,
  messageInsertCore,
  messageAddReplySchema,
  messageAddReplyCore,
  messageAddInlineReplySchema,
  messageAddInlineReplyCore,
  messageUpdateSchema,
  messageUpdateCore,
  // Phase 7b PR D1 — atomic message+attachments (messages family)
  attachmentInsertSchema,
  messageInsertWithAttachmentsSchema,
  messageInsertWithAttachmentsCore,
  messageAddReplyWithAttachmentsSchema,
  messageAddReplyWithAttachmentsCore,
  messageAddInlineReplyWithAttachmentsSchema,
  messageAddInlineReplyWithAttachmentsCore,
} from "./messages";
// Q&A payload contract — shared with the client reader (ThreadPanel).
export type { ThreadQaPayload } from "./thread-qa";
export {
  actionSendMessageSchema,
  actionSendMessageCore,
  actionReplyToThreadSchema,
  actionReplyToThreadCore,
  actionEditMessageSchema,
  actionEditMessageCore,
  actionSendCallEventSchema,
  actionSendCallEventCore,
  actionPostSystemLogSchema,
  actionPostSystemLogCore,
  // PR4 (Discord-shape) — "Create thread" posts a system announcement
  // citing the source message; server extension fans out post-insert
  // effects (notifications + unread bumps) via this exported core.
  actionCreateThreadSchema,
  actionCreateThreadCore,
  // Phase 7b PR D1 — atomic message+attachments (actions family, iOS path)
  actionSendMessageWithAttachmentsSchema,
  actionSendMessageWithAttachmentsCore,
  actionReplyToThreadWithAttachmentsSchema,
  actionReplyToThreadWithAttachmentsCore,
  // P5 (channel_members.last_read_at split) — canonical mark-read entry point
  markChannelReadSchema,
  markChannelReadCore,
} from "./actions";
// FIX A wiring — addReaction core + schema for the server-mutator override in
// `server/server-mutator-message-actions.ts`, which fires the dormant reaction-
// notification producer on a fresh reaction. `engagementActionMutators` exported
// for completeness alongside the core.
export {
  actionAddReactionSchema,
  addReactionCore,
  engagementActionMutators,
} from "./actions-engagement";
// WS-B6 v2c — workspace bootstrap core for the inline Cloudflare-KV
// write override in `server/server-mutators.ts`.
export {
  createWorkspaceBootstrapSchema,
  createWorkspaceBootstrapCore,
  type CreateWorkspaceBootstrapResult,
} from "./actions-bootstrap";
// Phase B remove-member dual-write — shared core + schema for the
// `workspace_members.delete` server-mutator override.
export {
  workspaceMemberDeleteCore,
  workspaceMemberDeleteSchema,
  type WorkspaceMemberDeleteResult,
  // Role-upgrade notification — shared core + schema for the
  // `workspace_members.update` server-mutator override.
  workspaceMemberUpdateCore,
  workspaceMemberUpdateSchema,
  type WorkspaceMemberUpdateResult,
} from "./workspaces";
// B-2: invite create + accept Zero-mutator path. Schemas + thin shared
// cores; the heavyweight side effects live in
// `server/server-mutators.ts` (token mint, admin gate, sibling revoke,
// transactional email, D1 mirror).
export {
  inviteInsertSchema,
  inviteInsertCore,
  type InviteInsertArgs,
} from "./invites";
export {
  acceptInviteSchema,
  acceptInviteCore,
  type AcceptInviteArgs,
} from "./actions-invites";
// Public-workspace request-to-join: shared row builder used by the
// regional API endpoint (`server/routes/public-workspace-routes.ts`)
// when inserting into `workspace_join_requests` via raw SQL. Plus the
// shared decide core consumed by the server-mutator extension to fire
// the approve-side side effects (member insert + notification).
export {
  requestJoinSchema,
  buildJoinRequestRow,
  decideJoinRequestSchema,
  decideJoinRequestCore,
  cancelJoinRequestSchema,
  type RequestJoinArgs,
  type DecideJoinRequestArgs,
  type DecideJoinRequestResult,
} from "./workspace-join-requests";
// Workspace-slug validation — single source of truth for charset/length/
// reserved-word rules. Re-exported so the regional bootstrap route
// (`server/routes/workspace-bootstrap-write-routes.ts`) can gate reserved
// slugs server-side without duplicating the reserved list.
export { assertWorkspaceSlugValid };
